import { useCallback, useEffect, useRef, useState } from 'react';
import type { MaxsoldAuctionDto } from '@jordan/types';
import { Alert, Spinner } from '@jordan/ui';
import { useGoogleMaps, type GMap, type GMarker, type GInfoWindow, type GGeocoder } from './useGoogleMaps';
import { auctionPlace, escapeHtml, formatClose, formatDistance } from './maxsoldSearchUtils';

interface MapProps {
  apiKey: string | null;
  mapsConfigured: boolean;
  auctions: MaxsoldAuctionDto[];
  center: { lat: number; lng: number };
  /** Open the auction's detail page (in-app SPA navigation from the popup). */
  onOpenAuction: (id: number) => void;
}

/** InfoWindow body for one auction: a DOM node so the "View lots" button can drive SPA navigation. */
function buildInfoNode(a: MaxsoldAuctionDto, onOpenAuction: (id: number) => void): HTMLElement {
  const meta = [auctionPlace(a), formatDistance(a.distanceMeters)].filter((s) => s && s !== '—').join(' · ');
  const stats = `${a.lotCount} lots · ${a.bidCount} bids · closes ${formatClose(a.closeTime)}`;
  const el = document.createElement('div');
  el.style.cssText = 'max-width:240px;font-size:13px;line-height:1.4';
  el.innerHTML = `
    ${a.imageUrls[0] ? `<img src="${escapeHtml(a.imageUrls[0])}" alt="" style="width:100%;height:120px;object-fit:cover;border-radius:6px;margin-bottom:8px" />` : ''}
    <div style="font-weight:600;margin-bottom:2px">${escapeHtml(a.title)}</div>
    <div style="color:#64748b">${escapeHtml(meta)}</div>
    <div style="color:#64748b;margin-bottom:8px">${escapeHtml(stats)}</div>`;

  const view = document.createElement('button');
  view.type = 'button';
  view.className = 'btn btn-primary btn-sm';
  view.textContent = 'View lots';
  view.addEventListener('click', () => onOpenAuction(a.id));
  el.appendChild(view);

  const links = document.createElement('div');
  links.style.cssText = 'margin-top:6px';
  links.innerHTML = [
    a.url ? `<a href="${escapeHtml(a.url)}" target="_blank" rel="noopener">MaxSold</a>` : '',
    a.mapUrl ? `<a href="${escapeHtml(a.mapUrl)}" target="_blank" rel="noopener">Directions</a>` : '',
  ]
    .filter(Boolean)
    .join(' &middot; ');
  el.appendChild(links);
  return el;
}

/** Google Maps view: one marker per auction, popup on click, auto-fit to the results. */
export function MaxsoldSearchMap({ apiKey, mapsConfigured, auctions, center, onOpenAuction }: MapProps) {
  const { google, loaded, error } = useGoogleMaps(apiKey);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<GMap | null>(null);
  const markersRef = useRef<GMarker[]>([]);
  const infoRef = useRef<GInfoWindow | null>(null);

  // Create the map once the API is ready.
  useEffect(() => {
    if (!google || !loaded || !containerRef.current || mapRef.current) return;
    mapRef.current = new google.maps.Map(containerRef.current, {
      center,
      zoom: 9,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    });
    infoRef.current = new google.maps.InfoWindow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [google, loaded]);

  // (Re)draw markers whenever the results change.
  useEffect(() => {
    const map = mapRef.current;
    if (!google || !map) return;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    infoRef.current?.close();

    const bounds = new google.maps.LatLngBounds();
    let lastPos: { lat: number; lng: number } | null = null;
    for (const a of auctions) {
      if (a.lat == null || a.lng == null) continue;
      const position = { lat: a.lat, lng: a.lng };
      const marker = new google.maps.Marker({ position, map, title: a.title });
      marker.addListener('click', () => {
        infoRef.current?.setContent(buildInfoNode(a, onOpenAuction));
        infoRef.current?.open({ anchor: marker, map });
      });
      markersRef.current.push(marker);
      bounds.extend(position);
      lastPos = position;
    }

    const placed = markersRef.current.length;
    if (placed > 1) {
      map.fitBounds(bounds, 48);
    } else if (placed === 1 && lastPos) {
      map.setCenter(lastPos);
      map.setZoom(12);
    } else {
      map.setCenter(center);
      map.setZoom(9);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [google, auctions]);

  // Recenter when the search centre moves but there are no results to frame.
  useEffect(() => {
    if (mapRef.current && !auctions.some((a) => a.lat != null)) mapRef.current.panTo(center);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center.lat, center.lng]);

  if (!mapsConfigured) {
    return (
      <Alert variant="info" icon="ph-map-trifold">
        The map needs a Google Maps key. Add <code>GOOGLE_MAPS_API_KEY</code> to the server <code>.env</code> to enable
        it — the List and Grid views work without it.
      </Alert>
    );
  }
  if (error) {
    return (
      <Alert variant="warning" icon="ph-warning">
        {error}
      </Alert>
    );
  }

  return (
    <div className="position-relative" style={{ height: '70vh', minHeight: 420 }}>
      {!loaded && (
        <div className="position-absolute top-50 start-50 translate-middle">
          <Spinner />
        </div>
      )}
      <div ref={containerRef} className="rounded" style={{ width: '100%', height: '100%' }} />
    </div>
  );
}

interface PlaceMatch {
  label: string;
  lat: number;
  lng: number;
}
interface PlaceSearchProps {
  apiKey: string | null;
  /** Bias results to a country ('canada' | 'usa'). */
  country?: string;
  placeholder?: string;
  onPick: (pick: PlaceMatch) => void;
}

const COUNTRY_CODE: Record<string, string> = { canada: 'ca', usa: 'us' };

/**
 * A location box that geocodes what you type and shows a dropdown of matching addresses; picking one
 * hands its lat/lng back to the caller. Uses the Geocoding API (core Maps JS) rather than the
 * deprecated Places Autocomplete widget. Disabled (with a hint) until Maps loads.
 */
export function MaxsoldPlaceSearch({ apiKey, country, placeholder, onPick }: PlaceSearchProps) {
  const { google, loaded, error } = useGoogleMaps(apiKey);
  const [text, setText] = useState('');
  const [matches, setMatches] = useState<PlaceMatch[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const geocoderRef = useRef<GGeocoder | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (google && !geocoderRef.current) geocoderRef.current = new google.maps.Geocoder();
  }, [google]);

  const runSearch = useCallback(
    (q: string) => {
      const geocoder = geocoderRef.current;
      if (!geocoder || q.trim().length < 3) {
        setMatches([]);
        setOpen(false);
        return;
      }
      setSearching(true);
      const cc = country ? COUNTRY_CODE[country] : undefined;
      geocoder
        .geocode({ address: q, ...(cc ? { componentRestrictions: { country: cc } } : {}) })
        .then(({ results }) => {
          setMatches(
            results.slice(0, 6).map((r) => ({
              label: r.formatted_address,
              lat: r.geometry.location.lat(),
              lng: r.geometry.location.lng(),
            })),
          );
          setOpen(true);
        })
        .catch(() => {
          setMatches([]);
          setOpen(false);
        })
        .finally(() => setSearching(false));
    },
    [country],
  );

  const onChange = (v: string) => {
    setText(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runSearch(v), 350);
  };

  const pick = (m: PlaceMatch) => {
    onPick(m);
    setText(m.label);
    setMatches([]);
    setOpen(false);
  };

  const placeholderText = placeholder ?? (loaded ? 'Search a place or postal code…' : 'Loading map search…');

  return (
    <div className="position-relative">
      <div className="form-control-feedback form-control-feedback-start">
        <input
          type="search"
          className="form-control"
          value={text}
          placeholder={placeholderText}
          disabled={!loaded}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => matches.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (matches[0]) pick(matches[0]);
              else runSearch(text);
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
        />
        <div className="form-control-feedback-icon">
          <i className={`${searching ? 'ph-circle-notch ph-spin' : 'ph-map-pin'} opacity-50`} />
        </div>
      </div>

      {open && matches.length > 0 && (
        <ul
          className="dropdown-menu show w-100 shadow"
          style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1056, maxHeight: 280, overflowY: 'auto' }}
        >
          {matches.map((m) => (
            <li key={`${m.label}-${m.lat}-${m.lng}`}>
              <button
                type="button"
                className="dropdown-item d-flex align-items-start gap-2"
                style={{ whiteSpace: 'normal' }}
                // mousedown (not click) so it fires before the input's blur closes the dropdown.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(m);
                }}
              >
                <i className="ph-map-pin text-muted mt-1" />
                <span>{m.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <div className="form-text text-warning">{error}</div>}
    </div>
  );
}
